import { useState, useEffect, useContext, useCallback } from "react";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const useDashboard = () => {
    const [attendants, setAttendants] = useState([]);
    const [loading, setLoading] = useState(false);
    const { user, socket } = useContext(AuthContext);

    const find = async (params) => {
        const { data } = await api.request({
            url: `/dashboard`,
            method: 'GET',
            params
        });
        return data;
    }

    const getReport = async (params) => {
        const { data } = await api.request({
            url: `/ticketreport/reports`,
            method: 'GET',
            params
        });
        return data;
    }

    // Função para atualizar apenas os tempos calculados localmente
    const updateTimestamps = useCallback(() => {
        setAttendants(prevAttendants => 
            prevAttendants.map(attendant => {
                if (attendant.online && attendant.lastLoginAt) {
                    const now = new Date();
                    const loginTime = new Date(attendant.lastLoginAt);
                    const currentSessionDuration = Math.floor((now - loginTime) / (1000 * 60)); // em minutos
                    
                    return {
                        ...attendant,
                        currentSessionDuration
                    };
                }
                return attendant;
            })
        );
    }, []);

    // Timer para atualizar tempos das sessões ativas em tempo real
    useEffect(() => {
        const interval = setInterval(() => {
            updateTimestamps();
        }, 1000); // Atualiza a cada segundo

        return () => clearInterval(interval);
    }, [updateTimestamps]);

    // Função para fazer fetch completo dos dados
    const fetchData = useCallback(async (params) => {
        setLoading(true);
        try {
            const data = await find(params);
            
            if (Array.isArray(data.attendants)) {
                setAttendants(data.attendants);
            } else {
                setAttendants([]);
            }
            
            setLoading(false);
            return data;
        } catch (error) {
            setLoading(false);
            throw error;
        }
    }, []);

    // Socket para atualizações em tempo real de usuários
    useEffect(() => {
        if (user.companyId && socket) {
            const companyId = user.companyId;

            const onUserUpdate = (data) => {
                if (data.action === "update") {
                    setAttendants(prevAttendants => 
                        prevAttendants.map(attendant => 
                            attendant.id === data.user.id 
                                ? { ...attendant, ...data.user }
                                : attendant
                        )
                    );
                }
            };

            // Socket listener for real-time login/logout events
            const onUserStatus = (data) => {
                setAttendants(prevAttendants => 
                    prevAttendants.map(attendant => {
                        if (attendant.id === data.userId) {
                            if (data.action === "user-login") {
                                return {
                                    ...attendant,
                                    online: true,
                                    lastLoginAt: data.timestamp,
                                    lastOnlineAt: data.timestamp
                                };
                            } else if (data.action === "user-logout") {
                                // Calcular duração da sessão que acabou de terminar
                                let lastSessionDuration = null;
                                if (attendant.lastLoginAt) {
                                    const loginTime = new Date(attendant.lastLoginAt);
                                    const logoutTime = new Date(data.timestamp);
                                    lastSessionDuration = Math.floor((logoutTime - loginTime) / (1000 * 60)); // em minutos
                                }
                                
                                return {
                                    ...attendant,
                                    online: false,
                                    lastLogoutAt: data.timestamp,
                                    lastOnlineAt: data.timestamp,
                                    lastSessionDuration: lastSessionDuration
                                };
                            }
                        }
                        return attendant;
                    })
                );
            };

            const userEventName = `company-${companyId}-user`;
            const statusEventName = `company-${companyId}-user-status`;

            socket.on(userEventName, onUserUpdate);
            socket.on(statusEventName, onUserStatus);

            return () => {
                socket.off(userEventName, onUserUpdate);
                socket.off(statusEventName, onUserStatus);
            };
        }
    }, [socket, user.companyId]);

    return {
        find,
        getReport,
        fetchData,
        updateTimestamps,
        attendants,
        setAttendants,
        loading
    }
}

export default useDashboard;